import type { ActivityLogicPlugin } from '../../core/types/plugin.base.types';
import type { NumberLinePayload, NumberLineRuntime } from '../../core/types/payloads/number_line.types';
import type { HostActionPayload, StudentInputEnvelope } from '../../core/types/events.types';
import type { RoutingMode } from '../../core/types/room.types';
import { NumberLinePayloadSchema } from '../../core/schemas/examGenerationSchema';

export function buildNumberLineTicks(start: number, end: number, step: number): number[] {
  const ticks: number[] = [];
  for (let value = start; value <= end; value += step) {
    ticks.push(value);
  }
  return ticks;
}

export function validateNumberLinePayload(raw: unknown): NumberLinePayload {
  const parsed = NumberLinePayloadSchema.safeParse(raw);
  if (!parsed.success) {
    const first = parsed.error.issues[0];
    throw new Error(first?.message || 'NumberLine payload invalid');
  }

  const data = parsed.data;
  const ticks = new Set(buildNumberLineTicks(data.start, data.end, data.step));
  const missingNumbers = [...new Set(data.missingNumbers)].sort((a, b) => a - b);

  for (const missing of missingNumbers) {
    if (!ticks.has(missing)) {
      throw new Error(`missingNumbers: ${missing} no está en la secuencia de la recta`);
    }
  }

  return {
    ...(data.instruction?.trim() ? { instruction: data.instruction.trim() } : {}),
    start: data.start,
    end: data.end,
    step: data.step,
    missingNumbers,
  };
}

export function createInitialNumberLineRuntime(
  _payload: NumberLinePayload,
  _routing: RoutingMode,
): NumberLineRuntime {
  return { viewed: false, answers: {} };
}

export const numberLineLogic: ActivityLogicPlugin<'number_line'> = {
  mode: 'number_line',
  validatePayload: validateNumberLinePayload,
  createInitialRuntime: createInitialNumberLineRuntime,

  onStudentInput(ctx, input: StudentInputEnvelope) {
    if (input.action === 'EXAM_CONTINUE' || input.action === 'VIEW') {
      return { runtime: { ...ctx.runtime, viewed: true } };
    }
    if (input.action === 'SUBMIT') {
      const data =
        input.data && typeof input.data === 'object'
          ? (input.data as Record<string, unknown>)
          : {};
      const answers = { ...(ctx.runtime.answers || {}) };
      for (const [key, value] of Object.entries(data)) {
        answers[String(key)] = String(value ?? '').trim();
      }
      return { runtime: { ...ctx.runtime, viewed: true, answers } };
    }
    return { runtime: ctx.runtime, rejected: true, rejectReason: 'UNKNOWN_ACTION' };
  },

  onHostAction(ctx, _action: HostActionPayload) {
    return { runtime: ctx.runtime };
  },
};

export function demoNumberLinePayload(): NumberLinePayload {
  return validateNumberLinePayload({
    instruction: 'Completa los números que faltan',
    start: 0,
    end: 10,
    step: 2,
    missingNumbers: [4, 8],
  });
}
